import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrochip, faUsers, faBatteryQuarter, faTriangleExclamation, faPlugCircleXmark, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import api from "../lib/api.js";
import { relativeDate } from "../lib/dates.js";

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("dashboard").json(),
    refetchInterval: 30_000,
  });

  if (isLoading) return <div className="p-6 data text-sm" style={{ color: "var(--color-muted)" }}>Loading…</div>;
  if (error) return <div className="p-6 data text-sm" style={{ color: "var(--color-red)" }}>Error: {error.message}</div>;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>Dashboard</h1>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card p-5 flex items-center gap-4">
          <FontAwesomeIcon icon={faUsers} style={{ color: "var(--color-muted)", fontSize: 18 }} />
          <div>
            <div className="data text-3xl font-bold" style={{ color: "var(--color-white)" }}>{data.total_users}</div>
            <div className="data text-xs" style={{ color: "var(--color-muted)" }}>Users</div>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <FontAwesomeIcon icon={faMicrochip} style={{ color: "var(--color-muted)", fontSize: 18 }} />
          <div>
            <div className="data text-3xl font-bold" style={{ color: "var(--color-white)" }}>{data.total_devices}</div>
            <div className="data text-xs" style={{ color: "var(--color-muted)" }}>Devices</div>
          </div>
        </div>
      </div>

      {/* Alert lists — three columns */}
      <div className="grid grid-cols-3 gap-4">
        <AlertList
          title="Critical Battery"
          icon={faBatteryQuarter}
          iconColor="var(--color-amber)"
          items={data.critical_battery}
          empty="All batteries nominal"
          renderItem={(d) => (
            <div className="flex items-center justify-between">
              <span className="data text-sm" style={{ color: "var(--color-white)" }}>{d.name}</span>
              <span className="data text-sm" style={{ color: "var(--color-amber)" }}>{d.battery_level}%</span>
            </div>
          )}
        />
        <AlertList
          title="Missing Data"
          icon={faTriangleExclamation}
          iconColor="var(--color-red)"
          items={data.missing_data}
          empty="All devices reporting"
          renderItem={(d) => (
            <div>
              <div className="data text-sm" style={{ color: "var(--color-white)" }}>{d.name}</div>
              <div className="data text-xs" style={{ color: "var(--color-muted)" }}>
                {d.last_seen_at ? relativeDate(d.last_seen_at) : "never seen"}
              </div>
            </div>
          )}
        />
        <AlertList
          title="Inactive"
          icon={faPlugCircleXmark}
          iconColor="var(--color-muted)"
          items={data.inactive}
          empty="All devices active"
          renderItem={(d) => (
            <div>
              <div className="data text-sm" style={{ color: "var(--color-white)" }}>{d.name}</div>
              <div className="data text-xs" style={{ color: "var(--color-muted)" }}>
                {d.last_seen_at ? relativeDate(d.last_seen_at) : "never seen"}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function AlertList({ title, icon, iconColor, items = [], empty, renderItem }) {
  const hasAlerts = items.length > 0;
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon && <FontAwesomeIcon icon={icon} style={{ color: iconColor || "var(--color-muted)", fontSize: 13 }} />}
          <span className="data text-xs uppercase" style={{ color: "var(--color-muted)", letterSpacing: "0.1em" }}>
            {title}
          </span>
        </div>
        {hasAlerts && (
          <span className="data font-bold" style={{ color: "var(--color-red)", fontSize: 16 }}>
            {items.length}
          </span>
        )}
      </div>
      {!hasAlerts ? (
        <div className="flex items-center gap-2 data text-xs" style={{ color: "var(--color-green, #22c55e)" }}>
          <FontAwesomeIcon icon={faCircleCheck} />
          {empty}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <div key={d.id}>{renderItem(d)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
